/**
 * Нижняя панель разделов на телефоне (docs/08-mobile-roadmap.md, M1).
 *
 * Как и шапка, живёт в AppShell и не размонтируется при переходах (§3.4).
 * Разделы и права — из useAppSections, те же, что у вкладок шапки;
 * четвёртая вкладка — профиль.
 *
 * Панель прячется, пока открыта экранная клавиатура: иначе в Chrome для
 * Android она поднимается вместе с клавиатурой и отнимает место у поля.
 * Высоту панели экраны берут из --tabbar-h (index.css); на время
 * клавиатуры она обнуляется атрибутом data-keyboard на <html>.
 *
 * Полноэкранные слои (карточка задачи, поиск, диалоги) лежат выше по
 * z-index и закрывают панель сами.
 */

import { UserRound } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { useVirtualKeyboardOpen } from "@/shared/lib/keyboard";
import { cn } from "@/shared/ui";

import { isSectionActive, useAppSections, type AppSection } from "./sections";

const PROFILE: AppSection = {
  key: "profile",
  to: "/settings/profile",
  label: "Профиль и уведомления",
  shortLabel: "Профиль",
  icon: UserRound,
  sections: ["/settings/profile"],
  stay: false,
};

export function BottomNav() {
  const { pathname } = useLocation();
  const sections = useAppSections();
  const keyboard = useVirtualKeyboardOpen();

  useEffect(() => {
    const root = document.documentElement;
    if (keyboard) root.dataset.keyboard = "open";
    else delete root.dataset.keyboard;
    return () => {
      delete root.dataset.keyboard;
    };
  }, [keyboard]);

  if (keyboard) return null;

  return (
    <nav
      aria-label="Основные разделы"
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface pr-(--safe-right) pb-(--safe-bottom) pl-(--safe-left)"
    >
      {[...sections, PROFILE].map((item) => {
        const active = isSectionActive(pathname, item.sections);
        const Icon = item.icon;
        return (
          <Link
            key={item.key}
            to={item.to}
            onClick={item.stay ? (event) => event.preventDefault() : undefined}
            aria-label={item.label}
            aria-current={active ? "page" : undefined}
            className={cn(
              "relative flex h-14 min-w-0 flex-1 flex-col items-center justify-center gap-0.5",
              "text-[11px] font-semibold transition-colors",
              active ? "text-fg" : "text-muted",
            )}
          >
            {active && (
              <span className="absolute top-0 right-1/4 left-1/4 h-0.5 bg-accent" aria-hidden />
            )}
            <Icon className={cn("size-5", active && "text-accent")} aria-hidden />
            <span className="max-w-full truncate px-1">{item.shortLabel}</span>
          </Link>
        );
      })}
    </nav>
  );
}
